import { IconButton, Menu, MenuItem } from "@thekrystalship/krystal";
import { useConfirmAction } from "./ServerActions.jsx";
import { serverCallRefusal } from "../lib/persona.js";

// Whether this person may send `action` for `player` on `server` — the moderation request itself,
// asked of the node that answers it — and the sentence when not.
function moderationRefusal(server, player, action) {
  if (!server || !player) return null;
  return serverCallRefusal(server, "POST", "/players/" + encodeURIComponent(player.playerIdentity || "_") + "/" + action);
}

// PlayerModeration — the per-row kick/ban/unban controls in the player roster.
//
// The contract that shapes this file: **the browser never names who gets
// moderated.** Every call sends only the roster's `playerIdentity`; the API
// resolves that against its own record of who has been on this server and builds
// the game's command from it. So there is deliberately no address or name in any
// request here, and nothing in this component should start adding one.
//
// What the game supports comes from the roster response's `moderation` block
// ({ kick, ban, unban, targetKind }) — the backend derives it from the
// blueprint's declared command templates. We render only what it reports, so a
// game with no ban command shows no ban button rather than one that 409s on
// click. `targetKind` names the identity the game addresses (ip | name | id); a
// player who carries no identity of that kind can't be moderated at all, and we
// say so on a disabled control instead of letting the click fail.
//
// Two presentations of the same action set: square icon buttons (the design
// system's IconButton) on desktop, and a "⋯" menu on phones, where the
// menu keeps both icon AND label. CSS picks which trigger is visible; both drive
// the same `onRun`.
//
// An action the game declares is always RENDERED, and disabled when the moment
// is wrong — a control that vanishes tells the operator nothing, while a
// disabled one that says why is the difference between "this game can't" and
// "not right now". The three gates, broadest first: the server has to be running
// (moderation is a console command — a stopped server has no console, and the
// engine refuses the call outright), the player has to carry an identity of the
// kind the game addresses, and a kick needs them actually connected.

// Which field of a roster row satisfies the game's declared target kind. Mirrors
// the API's own resolution so the control state matches what the call would do —
// this is a pre-emptive courtesy, NOT the authority. The API re-resolves from
// its own record on every request and is free to refuse.
function hasTargetIdentity(player, targetKind) {
  if (!player || !targetKind) return false;
  if (targetKind === "ip") return !!player.playerAddr;
  if (targetKind === "name") return !!player.playerName;
  if (targetKind === "id") return !!player.playerId;
  return false;
}

const KIND_LABEL = { ip: "an IP address", name: "a player name", id: "an account id" };

/// What may be done to this player right now, and why not.
///
/// The counterpart to `verbGuard` for lifecycle: ONE answer to "can this run", so every surface
/// offering moderation refuses in the same words. The palette can moderate a scoped server's roster,
/// and a row that said "Kick" where this menu says "the server isn't running" would be two answers to
/// one question.
///
/// Returns `[{ action, reason }]` — reason `null` when it can run. An action the game does not
/// declare is absent entirely; an action it declares is always PRESENT and carries its reason, because
/// a control that vanishes tells an operator nothing while a disabled one that says why distinguishes
/// "this game can't" from "not right now".
function moderationOffers(serverRunning, player, moderation, server) {
  if (!moderation || !player) return [];
  const banned = player.status === "banned";
  const online = player.status === "online";

  // A banned player gets the counterpart action, never both — offering "ban" on someone already
  // banned states a change that wouldn't happen.
  const offered = [];
  if (banned) { if (moderation.unban) offered.push("unban"); }
  else {
    if (moderation.kick) offered.push("kick");
    if (moderation.ban) offered.push("ban");
  }
  if (!offered.length) return [];

  const usable = hasTargetIdentity(player, moderation.targetKind);
  const identityReason = "This game moderates by "
    + (KIND_LABEL[moderation.targetKind] || "an identity") + ", which this player has none of.";

  // Broadest gate first, so the reason names the thing the operator would have to change first:
  // holding the action at all, then the server, then the player.
  return offered.map((action) => ({
    action,
    reason: moderationRefusal(server, player, action)
      || (!serverRunning ? "The server isn't running, so there's no console to send this to."
      : !usable ? identityReason
        : action === "kick" && !online ? "This player isn't connected — there's nobody to disconnect."
          : null),
  }));
}

const ACTION = {
  kick: { label: "Kick", icon: "user-x", tone: "warn", confirm: true,
    pending: "Kicking…", title: "Kick — disconnect this player" },
  ban: { label: "Ban", icon: "ban", tone: "danger", confirm: true,
    pending: "Banning…", title: "Ban — disconnect and block this player" },
  // Restoring access is not destructive, so it doesn't need a misclick guard.
  unban: { label: "Unban", icon: "user-check", tone: "safe", confirm: false,
    pending: "Unbanning…", title: "Unban — lift this player's ban" },
};

// An action's tone, in the design system's words for the consequence it carries.
const TONE = { warn: "warning", danger: "danger", safe: "success" };

function ModerationButton({ action, disabled, reason, pending, onRun }) {
  const def = ACTION[action];
  const { armed, trigger } = useConfirmAction(() => onRun(action));

  const click = (e) => {
    e.stopPropagation();
    if (disabled || pending) return;
    if (def.confirm) trigger(); else onRun(action);
  };

  // An icon-only control has to carry its meaning in the tooltip and the
  // accessible name, and both have to follow the state — an armed button that
  // still announces "Ban" hides the fact that the next click commits.
  const title = pending ? def.pending
    : armed ? "Click again to confirm"
      : disabled && reason ? reason
        : def.title;
  const label = pending ? def.pending
    : armed ? "Confirm " + def.label.toLowerCase()
      : def.label;

  return (
    <IconButton tone={TONE[def.tone]} armed={armed} busy={pending} disabled={disabled}
      icon={armed ? "check" : def.icon} title={title} label={label} onClick={click} />
  );
}

// One row of the "⋯" menu. Arming keeps the menu OPEN and swaps the label in
// place, so the second tap lands on the same target the first one did — a menu
// that closed on arming would ask the operator to re-open it and aim again.
// `why` is this item's own reason for being disabled — rendered as a second line
// rather than a tooltip, since a touch screen never shows one.
function ModerationMenuItem({ action, why, pending, onRun }) {
  const def = ACTION[action];
  const { armed, trigger } = useConfirmAction(() => onRun(action));

  const select = (e) => {
    if (!def.confirm) { onRun(action); return; }
    if (!armed) e.preventDefault();
    trigger();
  };

  return (
    <MenuItem icon={armed ? "check" : def.icon} tone={def.tone === "danger" ? "danger" : undefined}
      className={armed ? "pmod-menu__item is-armed" : "pmod-menu__item"} disabled={!!why || pending} onSelect={select}>
      <span className="pmod-menu__text">
        <span>{pending ? def.pending : armed ? "Tap again to confirm" : def.label}</span>
        {why ? <span className="pmod-menu__why">{why}</span> : null}
      </span>
    </MenuItem>
  );
}

// player: the roster row · moderation: the capability block · serverRunning: is
// the instance up · pending: the action currently in flight for THIS player (or
// null) · onRun(action)
function PlayerModeration({ server, player, moderation, serverRunning, pending, onRun }) {
  const items = moderationOffers(serverRunning, player, moderation, server);

  if (!items.length) return null;

  // A reason every action shares belongs at the top of the menu once, not
  // repeated under each item.
  const shared = items.every((i) => i.reason && i.reason === items[0].reason) ? items[0].reason : null;

  return (
    <span className="pmod">
      <span className="pmod__inline">
        {items.map((i) => (
          <ModerationButton key={i.action} action={i.action} disabled={!!i.reason} reason={i.reason}
            pending={pending === i.action} onRun={onRun} />
        ))}
      </span>

      <span className="pmod__compact">
        <Menu placement="bottom-end" header={shared}
          trigger={(
            <IconButton icon="ellipsis" className="pmod-kebab"
              label={"Moderate " + (player.playerName || player.playerIdentity)}
              onClick={(e) => e.stopPropagation()} />
          )}>
          {items.map((i) => (
            <ModerationMenuItem key={i.action} action={i.action} why={shared ? null : i.reason}
              pending={pending === i.action} onRun={onRun} />
          ))}
        </Menu>
      </span>
    </span>
  );
}

export { PlayerModeration, hasTargetIdentity, moderationOffers, moderationRefusal };
